import { useEffect, useState } from "react";
import { DropZone } from "../components/DropZone";
import { ResultPanel } from "../components/ResultPanel";
import { ChartReproduction } from "../components/ChartReproduction";
import { extractChart, type ExtractionResult, type ApiError } from "../lib/api";
import { track } from "../lib/plausible";

interface UiState {
  status: "idle" | "extracting" | "ok" | "error";
  imageSrc?: string;
  fileName?: string;
  result?: ExtractionResult;
  error?: string;
  remaining?: number | null;
  paid: boolean;
  rateLimited?: boolean;
}

export function Home() {
  const [state, setState] = useState<UiState>({ status: "idle", paid: false });
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const t = localStorage.getItem("session");
    if (t) setToken(t);
  }, []);

  const onFile = async (file: File) => {
    track("extract_start", { size_kb: Math.round(file.size / 1024) });
    const reader = new FileReader();
    reader.onload = () => {
      setState((s) => ({
        ...s,
        status: "extracting",
        imageSrc: reader.result as string,
        fileName: file.name,
      }));
    };
    reader.readAsDataURL(file);
    try {
      const res = await extractChart(file, token);
      track("extract_ok", { series: res.result.series.length });
      setState((s) => ({
        ...s,
        status: "ok",
        result: res.result,
        remaining: res.remaining_free,
        paid: res.paid,
      }));
    } catch (err) {
      const e = err as ApiError;
      track("extract_error", { status: e.status ?? 0 });
      setState((s) => ({
        ...s,
        status: "error",
        error: e.error ?? "Something went wrong",
        rateLimited: e.status === 429,
        remaining: e.remaining_free ?? s.remaining,
      }));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
      <section className="text-center max-w-2xl mx-auto space-y-3">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
          Paste a chart screenshot.
          <br />
          Get the data. Verify at a glance.
        </h1>
        <p className="text-ink/70 text-lg">
          Drag any chart into the page and download a CSV in 6 seconds. Powered
          by Claude Opus 4.7 vision. We re-render the extracted data as a clean
          chart alongside your original — if the two look the same, the
          extraction is right.
        </p>
      </section>

      <DropZone
        onFile={onFile}
        paid={state.paid}
        busy={state.status === "extracting"}
      />

      {state.remaining != null && !state.paid && (
        <p className="text-sm text-ink/60">
          {state.remaining} of 5 free conversions left today.{" "}
          <a href="/pricing" className="underline">
            Upgrade for unlimited
          </a>
          .
        </p>
      )}

      {state.status === "extracting" && (
        <div className="rounded-md border border-ink/10 p-6 text-center text-ink/70">
          Extracting chart data… this typically takes 4–8 seconds.
        </div>
      )}

      {state.status === "error" && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-900 space-y-2">
          <strong>Extraction failed:</strong> {state.error}
          {state.rateLimited && (
            <p>
              You've used your 5 free charts for today.{" "}
              <a href="/pricing" className="underline">
                Upgrade for unlimited
              </a>
              .
            </p>
          )}
        </div>
      )}

      {state.status === "ok" && state.result && state.imageSrc && state.fileName && (
        <section className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wide text-ink/60">Your chart</h3>
              <img
                src={state.imageSrc}
                alt="source chart"
                className="block w-full rounded-md border border-ink/10"
              />
            </div>
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wide text-ink/60">Extracted (re-rendered)</h3>
              <ChartReproduction result={state.result} />
            </div>
          </div>
          <ResultPanel result={state.result} fileName={state.fileName} />
        </section>
      )}

      <section className="grid md:grid-cols-3 gap-6 pt-12 border-t border-ink/10">
        <Feature
          title="One-shot extraction"
          body="Opus 4.7 high-resolution vision (98.5% visual acuity at 3.75 MP) reads small text in legends and footnotes — no preprocessing required."
        />
        <Feature
          title="Verification overlay"
          body="Extracted points re-plotted on top of your original chart. Misreads are obvious — no need to manually audit every series."
        />
        <Feature
          title="Public accuracy harness"
          body={
            <>
              30-chart fixture set scored vs ground-truth CSVs.{" "}
              <a href="/accuracy" className="underline">
                See the numbers
              </a>
              .
            </>
          }
        />
      </section>
    </div>
  );
}

function Feature({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm text-ink/70 leading-relaxed">{body}</p>
    </div>
  );
}
